import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { MapPin, Phone, Mail, Clock, Send } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Button } from '../components/ui/button';
import { hotelInfo } from '../data/mock';
import { toast } from 'sonner';

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: '',
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      // Send to backend API
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000';
      
      const response = await fetch(`${BACKEND_URL}/api/contact`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: formData.name || '',
          email: formData.email || '',
          phone: formData.phone || '',
          subject: formData.subject || '',
          message: formData.message || '',
        }),
      });
      
      if (response.ok) {
        // Prepare WhatsApp message
        const whatsappMessage = `*Contact Form Inquiry*
    
Name: ${formData.name || 'N/A'}
Email: ${formData.email || 'N/A'}
Phone: ${formData.phone || 'N/A'}
${formData.subject ? `Subject: ${formData.subject}` : ''}

Message:
${formData.message || 'N/A'}`;
        
        const whatsappNumber = (hotelInfo?.whatsapp || hotelInfo?.phone || '+911234567890').replace(/[^0-9]/g, '');
        const whatsappUrl = `https://wa.me/${whatsappNumber}?text=${encodeURIComponent(whatsappMessage)}`;
        
        // Open WhatsApp
        window.open(whatsappUrl, '_blank');
        
        toast.success('Message sent successfully! Email notification sent & opening WhatsApp...');
        
        // Reset form
        setFormData({ name: '', email: '', phone: '', subject: '', message: '' });
      } else {
        toast.error('Failed to send message. Please try again.');
      }
    } catch (error) {
      console.error('Contact error:', error);
      toast.error('An error occurred. Please try again or contact us directly.');
    }
  };

  return (
    <>
      <Helmet>
        <title>{`Contact Us | Hotel Suvidha Deluxe Haridwar`}</title>
        <meta name="description" content="Contact Hotel Suvidha Deluxe Haridwar for bookings and inquiries. Call +91-8941000583." />
      </Helmet>
    <div className="min-h-screen pt-32 pb-20">
      {/* Hero Section */}
      <section className="relative py-20 bg-gradient-to-br from-navy-900 via-navy-800 to-navy-900 text-white overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{ backgroundImage: 'url(https://images.unsplash.com/photo-1566073771259-6a8506099945?w=1920&q=80)', backgroundSize: 'cover', backgroundPosition: 'center' }}></div>
        </div>
        <div className="container mx-auto px-4 relative z-10 text-center">
          <h1 className="text-5xl md:text-6xl font-bold mb-6 font-serif text-navy-900">Contact Us</h1>
          <p className="text-xl text-gold-400 max-w-3xl mx-auto leading-relaxed">
            We're here to help make your stay memorable. Reach out to us anytime!
          </p>
        </div>
      </section>

      {/* Contact Content */}
      <section className="container mx-auto px-4 -mt-12">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Contact Info Cards */}
          <div className="lg:col-span-1 space-y-6">
            <Card className="border-0 shadow-xl">
              <CardContent className="p-6">
                <div className="w-14 h-14 bg-gold-100 rounded-full flex items-center justify-center mb-4">
                  <MapPin className="text-gold-600" size={28} />
                </div>
                <h3 className="text-xl font-bold text-navy-900 mb-2">Visit Us</h3>
                <p className="text-gray-600">{hotelInfo?.address || ', Haridwar'}</p>
              </CardContent>
            </Card>

            <Card className="border-0 shadow-xl">
              <CardContent className="p-6">
                <div className="w-14 h-14 bg-gold-100 rounded-full flex items-center justify-center mb-4">
                  <Phone className="text-gold-600" size={28} />
                </div>
                <h3 className="text-xl font-bold text-navy-900 mb-2">Call Us</h3>
                <a href={`tel:${hotelInfo?.phone || '+911234567890'}`} className="text-gray-600 hover:text-gold-600 transition-colors">
                  {hotelInfo?.phone || '+91-8941000583'}
                </a>
                <p className="text-sm text-gray-500 mt-2">
                  <a href={`https://wa.me/${(hotelInfo?.whatsapp || hotelInfo?.phone || '+911234567890').replace(/[^0-9]/g, '')}`} className="text-green-600 hover:text-green-700">
                    WhatsApp Available
                  </a>
                </p>
              </CardContent>
            </Card>

            <Card className="border-0 shadow-xl">
              <CardContent className="p-6">
                <div className="w-14 h-14 bg-gold-100 rounded-full flex items-center justify-center mb-4">
                  <Mail className="text-gold-600" size={28} />
                </div>
                <h3 className="text-xl font-bold text-navy-900 mb-2">Email Us</h3>
                <a href={`mailto:${hotelInfo?.email || 'info@example.com'}`} className="text-gray-600 hover:text-gold-600 transition-colors break-all">
                  {hotelInfo?.email || 'info@example.com'}
                </a>
              </CardContent>
            </Card>

            <Card className="border-0 shadow-xl">
              <CardContent className="p-6">
                <div className="w-14 h-14 bg-gold-100 rounded-full flex items-center justify-center mb-4">
                  <Clock className="text-gold-600" size={28} />
                </div>
                <h3 className="text-xl font-bold text-navy-900 mb-2">Hours</h3>
                <p className="text-gray-600">24/7 Reception</p>
                <p className="text-sm text-gray-500 mt-1">Always here to assist you</p>
              </CardContent>
            </Card>
          </div>

          {/* Contact Form */}
          <div className="lg:col-span-2">
            <Card className="border-0 shadow-xl">
              <CardContent className="p-8">
                <h2 className="text-3xl font-bold text-navy-900 mb-6 font-serif">Send Us a Message</h2>
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <Label htmlFor="name">Full Name *</Label>
                      <Input
                        id="name"
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        placeholder="Your name"
                        required
                      />
                    </div>
                    <div>
                      <Label htmlFor="email">Email Address *</Label>
                      <Input
                        id="email"
                        type="email"
                        value={formData.email}
                        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                        placeholder="your@email.com"
                        required
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <Label htmlFor="phone">Phone Number *</Label>
                      <Input
                        id="phone"
                        type="tel"
                        value={formData.phone}
                        onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                        placeholder="+91 1234567890"
                        required
                      />
                    </div>
                    <div>
                      <Label htmlFor="subject">Subject</Label>
                      <Input
                        id="subject"
                        value={formData.subject}
                        onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                        placeholder="How can we help?"
                      />
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="message">Message *</Label>
                    <Textarea
                      id="message"
                      value={formData.message}
                      onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                      placeholder="Tell us more about your inquiry..."
                      rows={6}
                      required
                    />
                  </div>

                  <Button type="submit" size="lg" className="w-full bg-gold-600 hover:bg-gold-700 text-white text-lg py-6">
                    <Send className="mr-2" size={20} />
                    Send Message
                  </Button>
                </form>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Map Section */}
      <section className="container mx-auto px-4 mt-20">
        <Card className="border-0 shadow-xl overflow-hidden">
          <div className="aspect-[21/9] bg-gray-200 relative">
            <iframe
              src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3462.073819516631!2d78.16331731508!3d29.945277081933!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x390833cb18d8e06b%3A0x9d5bf1e49e4a4c5e!2sHar%20Ki%20Pauri%2C%20Haridwar!5e0!3m2!1sen!2sin!4v1635234567890!5m2!1sen!2sin"
              width="100%"
              height="100%"
              style={{ border: 0 }}
              allowFullScreen=""
              loading="lazy"
              title="Hotel Location Map"
              className="absolute inset-0"
            ></iframe>
          </div>
        </Card>
      </section>
    </div>
    </>
  );
};

export default Contact;
